import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select as ChakraSelect,
  useToast,
  useColorModeValue,
  Icon,
  Center,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiUsers,
} from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

const AVAILABLE_COLORS = [
  'gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'cyan', 'purple', 'pink',
];

/**
 * Network Relationship Types Manager
 * 
 * Manage general relationship types (not tied to specific deals).
 * Examples: referral-partner, preferred-vendor, sphere, colleague
 * 
 * Configuration:
 * - Name and reciprocal name
 * - Color
 * - Description
 */
export default function NetworkRelationshipsManager() {
  const toast = useToast();
  const { networkRelationshipTypes, loading: settingsLoading } = useCrmSettings();
  const [selectedType, setSelectedType] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');

  const handleAdd = () => {
    setSelectedType(null);
    onOpen();
  };

  const handleEdit = (type) => {
    setSelectedType(type);
    onOpen();
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Are you sure you want to delete the "${type.label}" relationship type?`)) {
      return;
    }

    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      const updatedTypes = networkRelationshipTypes.filter(t => t.value !== type.value);

      const result = await updateCrmSettings({
        updates: {
          networkRelationshipTypes: updatedTypes,
        },
      });

      if (result.data.success) {
        toast({
          title: 'Relationship type deleted',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (settingsLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Network Relationship Types</Heading>
            <Text fontSize="sm" color="gray.600">
              Define general relationship types (not tied to specific deals)
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAdd}
          >
            Add Relationship Type
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              About Network Relationships
            </Text>
            <Text fontSize="xs">
              These are general professional connections not tied to specific deals. 
              Examples: "This agent is my referral partner" or "This is my preferred title company."
            </Text>
          </VStack>
        </Alert>

        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBG}>
              <Tr>
                <Th>Preview</Th>
                <Th>Value</Th>
                <Th>Label</Th>
                <Th>Reciprocal Type</Th>
                <Th>Color</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {networkRelationshipTypes?.map((type) => (
                <Tr key={type.value} _hover={{ bg: hoverBG }}>
                  <Td>
                    <Badge colorScheme={type.color}>{type.label}</Badge>
                  </Td>
                  <Td fontFamily="mono" fontSize="xs">{type.value}</Td>
                  <Td fontWeight="medium">{type.label}</Td>
                  <Td fontSize="sm">{type.reciprocal || '—'}</Td>
                  <Td>
                    <Badge colorScheme={type.color} variant="solid">
                      {type.color}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="ghost"
                        aria-label="Edit"
                        onClick={() => handleEdit(type)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => handleDelete(type)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      {/* Editor Modal */}
      <TypeEditorModal
        isOpen={isOpen}
        onClose={onClose}
        relationshipType={selectedType}
        existingTypes={networkRelationshipTypes}
      />
    </Box>
  );
}

function TypeEditorModal({ isOpen, onClose, relationshipType, existingTypes }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [reciprocal, setReciprocal] = useState('');
  const [color, setColor] = useState('purple');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (relationshipType) {
      setValue(relationshipType.value || '');
      setLabel(relationshipType.label || '');
      setReciprocal(relationshipType.reciprocal || '');
      setColor(relationshipType.color || 'purple');
    } else {
      setValue('');
      setLabel('');
      setReciprocal('');
      setColor('purple');
    }
  }, [relationshipType, isOpen]);

  const handleSave = async () => {
    if (!value.trim() || !label.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Value and label are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!/^[a-z0-9-]+$/.test(value)) {
      toast({
        title: 'Invalid value format',
        description: 'Value must be lowercase alphanumeric with hyphens only',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');

      let updatedTypes = [...(existingTypes || [])];

      const newType = {
        value: value.trim(),
        label: label.trim(),
        reciprocal: reciprocal.trim() || value.trim(), // Default to same type if not specified
        color,
      };

      if (relationshipType) {
        // Update existing
        const index = updatedTypes.findIndex(t => t.value === relationshipType.value);
        if (index > -1) {
          updatedTypes[index] = newType;
        }
      } else {
        // Add new
        if (updatedTypes.some(t => t.value === value.trim())) {
          toast({
            title: 'Duplicate value',
            description: 'A relationship type with this value already exists',
            status: 'error',
            duration: 3000,
          });
          setIsSaving(false);
          return;
        }
        updatedTypes.push(newType);
      }

      const result = await updateCrmSettings({
        updates: {
          networkRelationshipTypes: updatedTypes,
        },
      });

      if (result.data.success) {
        toast({
          title: relationshipType ? 'Updated' : 'Created',
          description: `Relationship type ${relationshipType ? 'updated' : 'created'} successfully`,
          status: 'success',
          duration: 2000,
        });
        onClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {relationshipType ? 'Edit' : 'Create'} Network Relationship Type
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Value (Unique ID)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., referral-partner"
                isDisabled={!!relationshipType}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Lowercase, hyphenated. Cannot be changed after creation.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Referral Partner"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Reciprocal Type</FormLabel>
              <Input
                value={reciprocal}
                onChange={(e) => setReciprocal(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., referral-partner (or leave for same type)"
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                When A→B relationship is created, B→A uses this reciprocal type. Leave blank to use same type.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Color</FormLabel>
              <ChakraSelect
                value={color}
                onChange={(e) => setColor(e.target.value)}
              >
                {AVAILABLE_COLORS.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </ChakraSelect>
              <HStack spacing={2} mt={2}>
                <Text fontSize="xs">Preview:</Text>
                <Badge colorScheme={color}>{label || 'Sample'}</Badge>
              </HStack>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {relationshipType ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}





